import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { WorkspacePaperView, Paper } from "@/types/models";
import type { RootState } from "@store/index";
import {
  addPaper,
  renamePaper as renamePaperReducer,
  removePaper as removePaperReducer,
} from "@store/slices/workspaceSlice";
import {
  createPaper as createPaperApi,
  renamePaper as renamePaperApi,
  deletePaper as deletePaperApi,
  getWorkspacePapersManage,
} from "@services/api";

interface WorkspacePapersState {
  allWorkspacePapers: WorkspacePaperView[];
  loading: boolean;
  error: string | null;
}

const initialState: WorkspacePapersState = {
  allWorkspacePapers: [],
  loading: false,
  error: null,
};

export const loadAllWorkspacePapers = createAsyncThunk<
  WorkspacePaperView[],
  string,
  { rejectValue: string }
>("workspacePapers/loadAll", async (workspaceId, { rejectWithValue }) => {
  const result = await getWorkspacePapersManage(workspaceId);
  if (!result.success) {
    return rejectWithValue(result.error.message || "Failed to load papers");
  }
  return result.data;
});

export const createPaper = createAsyncThunk<
  Paper,
  { workspaceId: string; paperName: string },
  { state: RootState; rejectValue: string }
>(
  "workspacePapers/create",
  async ({ workspaceId, paperName }, { dispatch, rejectWithValue }) => {
    const result = await createPaperApi(workspaceId, {
      paper_name: paperName,
    });
    if (!result.success) {
      return rejectWithValue(result.error.message || "Failed to create paper");
    }
    dispatch(addPaper(result.data));
    return result.data;
  }
);

export const renamePaper = createAsyncThunk<
  { paperId: string; paperName: string },
  { paperId: string; paperName: string },
  { state: RootState; rejectValue: string }
>(
  "workspacePapers/rename",
  async ({ paperId, paperName }, { getState, dispatch, rejectWithValue }) => {
    const workspaceId = getState().workspace.workspace_id;
    if (!workspaceId) return rejectWithValue("No active workspace");
    const result = await renamePaperApi(workspaceId, paperId, paperName);
    if (!result.success) {
      return rejectWithValue(result.error.message || "Failed to rename paper");
    }
    dispatch(renamePaperReducer({ paperId, paperName }));
    return { paperId, paperName };
  }
);

export const deletePaper = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: string }
>(
  "workspacePapers/delete",
  async (paperId, { getState, dispatch, rejectWithValue }) => {
    const workspaceId = getState().workspace.workspace_id;
    if (!workspaceId) return rejectWithValue("No active workspace");
    const result = await deletePaperApi(workspaceId, paperId);
    if (!result.success) {
      return rejectWithValue(result.error.message || "Failed to delete paper");
    }
    dispatch(removePaperReducer(paperId));
    return paperId;
  }
);

const workspacePapersSlice = createSlice({
  name: "workspacePapers",
  initialState,
  reducers: {
    clearWorkspacePapers: (state) => {
      state.allWorkspacePapers = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAllWorkspacePapers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loadAllWorkspacePapers.fulfilled,
        (state, action: PayloadAction<WorkspacePaperView[]>) => {
          state.loading = false;
          state.allWorkspacePapers = action.payload;
        }
      )
      .addCase(loadAllWorkspacePapers.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to load workspace papers";
      })
      .addCase(createPaper.fulfilled, (state, action) => {
        const p = action.payload;
        const view: WorkspacePaperView = {
          paper_id: p.paper_id,
          workspace_id: p.workspace_id,
          paper_name: p.paper_name,
          paper_data: p.paper_data,
          description: p.description,
          instructions: p.instructions,
          due_date: p.due_date,
          required: p.required ?? false,
          assignment_kind: (p as { assignment_kind?: string }).assignment_kind ?? "optional",
          assigned_by_user_id: p.assigned_by_user_id,
          assigned_by_username: null,
          assigned_user_count: 0,
          completed_count: 0,
          in_progress_count: 0,
          not_started_count: 0,
          my_status: null,
          my_points: null,
          my_max_points: null,
          my_submitted_at: null,
          created_at: p.created_at,
        };
        if (
          !state.allWorkspacePapers.some(
            (existing) => existing.paper_id === view.paper_id
          )
        ) {
          state.allWorkspacePapers.unshift(view);
        }
      })
      .addCase(renamePaper.fulfilled, (state, action) => {
        const { paperId, paperName } = action.payload;
        const entry = state.allWorkspacePapers.find(
          (p) => p.paper_id === paperId
        );
        if (entry) {
          entry.paper_name = paperName;
        }
      })
      .addCase(deletePaper.fulfilled, (state, action) => {
        state.allWorkspacePapers = state.allWorkspacePapers.filter(
          (p) => p.paper_id !== action.payload
        );
      });
  },
});

export const { clearWorkspacePapers } = workspacePapersSlice.actions;

export const selectAllWorkspacePapers = (state: RootState) =>
  state.workspacePapers.allWorkspacePapers;
export const selectWorkspacePapersLoading = (state: RootState) =>
  state.workspacePapers.loading;
export const selectWorkspacePapersError = (state: RootState) =>
  state.workspacePapers.error;

export default workspacePapersSlice.reducer;
